import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { LookupItem } from 'app/model/lookup.model';
import { CampNotesDetailComponent } from 'app/modules/admin/camp/detail/notes/detail/detail.component';
import { TodoService } from 'app/modules/admin/dashboard/todo/todo.service';
import { LookupService } from 'app/services/lookup.service';
import { filter, fromEvent, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'notifications',
    imports: [
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        NgClass,
        NgTemplateOutlet,
        RouterLink,
        DatePipe,
    ],
})
export class NotificationsComponent implements OnInit, OnDestroy {
    @ViewChild('notificationsOrigin') private _notificationsOrigin!: MatButton;
    @ViewChild('notificationsPanel')
    private _notificationsPanel!: TemplateRef<any>;

    users: LookupItem[] = [];
    notifications: Notification[] = [];
    unreadCount: number = 0;
    private _overlayRef!: OverlayRef;
    private _unsubscribeAll = new Subject<void>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _notificationsService: NotificationsService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _matDialog: MatDialog,
        private _todoService: TodoService,
        private _fuseConfirmationService: FuseConfirmationService,
        private _lookupService: LookupService,
        private _router: Router
    ) { }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {

        // START polling API
        this._notificationsService.startNotificationPolling();

        // Refresh on navigation
        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                this._notificationsService.getAll().subscribe();
            });

        // Refresh on focus
        fromEvent(document, 'visibilitychange')
            .pipe(
                filter(() => document.visibilityState === 'visible'),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                this._notificationsService.getAll().subscribe();
            });

        // Load notifications once
        this._notificationsService.getAll().subscribe();

        // load users
        this._lookupService.getUsers()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(users => {
                this.users = users;
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to notification changes
        this._notificationsService.notifications$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((notifications: Notification[]) => {
                // Load the notifications
                this.notifications = notifications;

                // Calculate the unread count
                this._calculateUnreadCount();

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // STOP polling API
        this._notificationsService.stopNotificationPolling();

        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the notifications panel
     */
    openPanel(): void {
        // Return if the notifications panel or its origin is not defined
        if (!this._notificationsPanel || !this._notificationsOrigin) {
            return;
        }

        // Create the overlay if it doesn't exist
        if (!this._overlayRef) {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(
            new TemplatePortal(this._notificationsPanel, this._viewContainerRef)
        );
    }

    /**
     * Close the notifications panel
     */
    closePanel(): void {
        this._overlayRef.detach();
    }

    /**
     * Edit the notification
     */
    edit(notification: any): void {
        const todo = notification.todo;
        // close notification panel
        if (this._overlayRef?.hasAttached()) {
            this._overlayRef.detach();
        }
        this._matDialog.open(CampNotesDetailComponent, {
            data: {
                campId: todo.campId,
                todo: todo,
                users: this.users
            }
        })
            .afterClosed()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(result => {
                if (!result) return;
                this._todoService.updateTodo(result)
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe({
                        next: () => {
                            this._todoService.getTodos().subscribe();
                            this._notificationsService.getAll().subscribe(); // refresh bell notifications
                            this._todoService.notifyTodoUpdate('update', result); // notify observers (like notes page)
                            this._changeDetectorRef.markForCheck();
                        }
                    });
            });
    }

    /**
     * Delete the given notification
     */
    delete(notification: any): void {

        const confirmation = this._fuseConfirmationService.open({
            title: 'Delete Note',
            message: `Are you sure you want to delete this "${notification.description}" note?`,
            actions: {
                confirm: { label: 'Delete' },
                cancel: { label: 'Cancel' }
            }
        });

        confirmation.afterClosed()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(result => {

                if (result !== 'confirmed') return;

                const index = this.notifications.findIndex(n => n.id === notification.id);
                if (index === -1) return;

                const removed = this.notifications[index];

                // Remove from UI
                this.notifications.splice(index, 1);
                this._calculateUnreadCount();
                this._changeDetectorRef.markForCheck();

                // DELETE TODO (this updates dashboard)
                this._todoService.deleteTodo(notification.todo.id)
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe({
                        next: () => {
                            // refresh dashboard todos
                            this._todoService.getTodos().subscribe();
                            this._notificationsService.getAll().subscribe(); // refresh bell notifications
                            this._todoService.notifyTodoUpdate('delete', undefined, notification.todo.id); // notify observers
                            this._changeDetectorRef.markForCheck();
                        },
                        error: () => {
                            // restore if API fails
                            this.notifications.splice(index, 0, removed);
                            this._calculateUnreadCount();
                            this._changeDetectorRef.markForCheck();
                        }
                    });

            });
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(
                    this._notificationsOrigin._elementRef.nativeElement
                )
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }

    /**
     * Calculate the unread count
     *
     * @private
     */
    private _calculateUnreadCount(): void {
        let count = 0;

        if (this.notifications && this.notifications.length) {
            count = this.notifications.filter(
                (notification) => !notification.read
            ).length;
        }

        this.unreadCount = count;
    }
}
